import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { ArrowUpIcon } from "@tento-ui/icon-exports";
import { EditableText } from "@tento-ui/components/editable-text";

const StopIcon = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="10" height="10" rx="2" fill="currentColor"/></svg>`;

@component({ name: "std-chat-composer" })
export class ChatComposer extends LonaWebComponent {
  private $input: EditableText = this.hydrate("input");
  private $sendBtn: HTMLButtonElement = this.$("send-btn");

  private onSend: Option<(text: string) => void>;
  private onStop: Option<() => void>;
  private streaming: boolean = false;

  constructor() {
    super();
    this.$sendBtn.innerHTML = ArrowUpIcon;

    GESTURE_MANAGER.addPointerEvent(this.$sendBtn, {
      onClick: () => {
        if (this.streaming) {
          this.onStop?.();
        } else {
          this.send();
        }
      },
    });

    this.$input.bind(null, "Type a message...");
    this.$input.enableEditing(false);

    this.$input.controller.onEnterKey = () => {
      if (this.streaming) return;
      this.send();
    };
  }

  bind(props: ChatComposer.Props): this {
    if (props.placeholder != null) {
      this.$input.$("root").setAttribute("placeholder", props.placeholder);
    }
    this.onSend = props.onSend ?? null;
    this.onStop = props.onStop ?? null;
    return this;
  }

  setStreaming(streaming: boolean): void {
    this.streaming = streaming;
    this.$sendBtn.innerHTML = streaming ? StopIcon : ArrowUpIcon;
    this.$sendBtn.toggleAttribute("streaming", streaming);
  }

  private send(): void {
    const text = this.$input.text?.trim() ?? "";
    if (!text) return;
    this.$input.bindText(null);
    this.onSend?.(text);
  }

  static $styles = [
    css`
      :host {
        display: block;
      }

      #container {
        display: flex;
        align-items: flex-end;
        border-radius: 24px;
        border: 1px solid var(--divider, #e0e0e0);
        padding: 4px 4px 4px 16px;
        transition: border-color 0.3s ease, box-shadow 0.3s ease;
        background: var(--bg, #fff);
        box-shadow: var(--box-shadow-very-light);
      }

      #container:focus-within {
        border-color: var(--bg-btn-pri);
        outline: none;
      }

      #input {
        flex: 1;
        min-width: 0;
        --font-size: var(--p-size);
        color: var(--text-pri, #000);
        align-self: center;
        margin-block: 8px;
      }

      #send-btn {
        width: 24px;
        height: 24px;
        margin: 2px;
        border-radius: 50%;
        background: var(--bg-btn-pri);
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        flex-shrink: 0;
        padding: 0;
        transition: opacity 0.3s ease;
      }

      #send-btn:focus {
        outline: none;
      }

      #send-btn:hover {
        background: var(--bg-btn-pri-hover);
      }

      #send-btn:active {
        background: var(--bg-btn-pri-hover);
      }

      #send-btn svg {
        width: 14px;
        height: 14px;
      }

      #send-btn[streaming] {
        background: var(--text-pri);
      }

      #send-btn[streaming]:hover {
        background: var(--text-sec);
      }
    `,
  ];

  static $html = html`
    <div id="container">
      <std-editable-text id="input"></std-editable-text>
      <button id="send-btn"></button>
    </div>
  `;
}

export namespace ChatComposer {
  export type Props = {
    placeholder?: Option<string>;
    onSend?: (text: string) => void;
    onStop?: () => void;
  };
}
